import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui'
import { ReportingEvent } from '@/features/project/types'

interface ReportingEventsCellProps {
  events: ReportingEvent[]
}

export const ReportingEventsCell = ({ events }: ReportingEventsCellProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const MAXIMUM_EVENTS_TO_SHOW = 3
  const MAXIMUM_EVENT_NAME_LENGTH = 25

  if (events.length === 0) return <span>-</span>

  const displayEvents = isExpanded
    ? events
    : events.slice(0, MAXIMUM_EVENTS_TO_SHOW)
  const remainingCount = events.length - MAXIMUM_EVENTS_TO_SHOW

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap gap-1">
        {displayEvents.map((event, index) => {
          const isLongText =
            event.reporting_event_name.length > MAXIMUM_EVENT_NAME_LENGTH

          const eventBadge = (
            <span className="inline-flex max-h-14 max-w-50 items-center px-sm py-xxs rounded-sm border border-solid border-[--Colors-Gray-light-300] bg-[--Colors-Base-white] text-sm text-gray-700">
              <span className="truncate">{event.reporting_event_name}</span>
            </span>
          )

          return isLongText ? (
            <Tooltip key={event.id || index}>
              <TooltipTrigger asChild>{eventBadge}</TooltipTrigger>
              <TooltipContent className="max-w-[250px] break-words">
                {event.reporting_event_name}
              </TooltipContent>
            </Tooltip>
          ) : (
            <span key={event.id || index}>{eventBadge}</span>
          )
        })}
        {!isExpanded && remainingCount > 0 && (
          <button
            className="inline-flex items-center px-sm py-xxs rounded-sm bg-[--Colors-Base-white] text-sm text-gray-700 border border-solid border-[--Colors-Gray-light-300] cursor-pointer hover:bg-gray-50"
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(true)
            }}
          >
            +{remainingCount}
          </button>
        )}
      </div>
      {isExpanded && events.length > MAXIMUM_EVENTS_TO_SHOW && (
        <div className="flex justify-end">
          <Button
            variant="text"
            color="primary"
            className="p-1 m-0 text-xs"
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(false)
            }}
          >
            {t('common.seeLess')}
          </Button>
        </div>
      )}
    </div>
  )
}
